import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Button,
    TextField,
    MenuItem,
    Paper,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';

function EmployerApplicationDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [decision, setDecision] = useState('');
    const [notes, setNotes] = useState('');

    const { data: application, isLoading, isError, error } = useQuery(
        ['employerApplication', id],
        () => JobHttpService.loadApplication(id),
        {
            enabled: !!id,
            onSuccess: (data) => {
                setDecision(data?.final_decision || '');
                setNotes(data?.decision_notes || '');
            }
        }
    );

    const cv = application?.cv;
    const cvFile = application?.cv_file;
    
    // Get CV ID - handle both object and ID number cases
    const cvId = cv?.id || (typeof cv === 'number' ? cv : null);

    // Load CV sections if CV exists - must be called before any early returns
    const { data: cvSections, isLoading: cvSectionsLoading } = useQuery(
        ['cvSections', cvId],
        async () => {
            if (!cvId) return null;
            const service = new HttpService();
            const [experiences, education, skills, languages, certificates, references] = await Promise.all([
                service.get(`/job/cv/jobexperience/?cv=${cvId}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/education/?cv=${cvId}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/userskill/?cv=${cvId}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/userlanguage/?cv=${cvId}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/certificate/?cv=${cvId}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/reference/?cv=${cvId}`, null).catch(() => ({ results: [] }))
            ]);
            return {
                experiences: Array.isArray(experiences) ? experiences : (experiences?.results || []),
                education: Array.isArray(education) ? education : (education?.results || []),
                skills: Array.isArray(skills) ? skills : (skills?.results || []),
                languages: Array.isArray(languages) ? languages : (languages?.results || []),
                certificates: Array.isArray(certificates) ? certificates : (certificates?.results || []),
                references: Array.isArray(references) ? references : (references?.results || [])
            };
        },
        { enabled: !!cvId }
    );

    const { mutate: saveDecision, isLoading: saving } = useMutation(
        (payload) => JobHttpService.updateApplication(id, payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['employerApplication', id]);
                queryClient.invalidateQueries(['jobApplications']);
            }
        }
    );

    const handleSave = () => {
        // Get job ID - handle both object and ID number cases
        const jobId = application?.job?.id || (typeof application?.job === 'number' ? application.job : null);
        
        saveDecision({
            job: jobId,
            final_decision: decision || null,
            decision_notes: notes || null
        });
    };

    const getCVFileUrl = () => {
        if (!cvFile?.file) return null;
        if (cvFile.file.startsWith('http')) return cvFile.file;
        if (cvFile.file.startsWith('/')) {
            return HttpService.SiteBase + cvFile.file;
        }
        return HttpService.SiteBase + '/' + cvFile.file;
    };

    if (isLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (isError || !application) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="error">
                        {error?.message || 'Failed to load application details'}
                    </Alert>
                </Container>
            </section>
        );
    }

    const user = application.user;
    const profile = user?.profile;
    const job = application.job;
    const fromApplications = location.state?.from === 'applications';

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            variant="outlined"
                            onClick={() => navigate('/employer/applications')}
                        >
                            Back to Applications
                        </Button>
                        {job?.id && (
                            <Button
                                startIcon={<WorkIcon />}
                                variant="text"
                                onClick={() => navigate(`/employer/jobs/${job.id}`)}
                            >
                                Go to Job
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={3}>
                        {/* Left Column - Candidate Info */}
                        <Grid item xs={12} md={8}>
                            {/* Candidate Information */}
                            <Card sx={{ mb: 3 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        Candidate Information
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <PersonIcon fontSize="small" color="action" />
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Name
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1">
                                                {profile?.first_name || ''} {profile?.last_name || ''}
                                                {!profile?.first_name && !profile?.last_name && user?.email?.split('@')[0]}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <EmailIcon fontSize="small" color="action" />
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Email
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1">
                                                {user?.email || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        {profile?.phone_number && (
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <PhoneIcon fontSize="small" color="action" />
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Phone
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body1">
                                                    {profile.phone_number}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {application.created_at && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Applied Date
                                                </Typography>
                                                <Typography variant="body1">
                                                    {new Date(application.created_at).toLocaleDateString()}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Job Information */}
                            {job && (
                                <Card sx={{ mb: 3 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h5" fontWeight={600} gutterBottom>
                                            Job Information
                                        </Typography>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            {job.title}
                                        </Typography>
                                        {job.company && (
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {job.company.title}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Application Details */}
                            {(application.title || application.company_title) && (
                                <Card sx={{ mb: 3 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Application Details
                                        </Typography>
                                        <Divider sx={{ my: 2 }} />
                                        <Grid container spacing={2}>
                                            {application.title && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Title
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {application.title}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {application.company_title && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Company Title
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {application.company_title}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Cover Letter */}
                            {application.cover_letter && (
                                <Card sx={{ mb: 3 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Cover Letter
                                        </Typography>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {application.cover_letter}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}

                            {/* CV Information */}
                            {cv && (
                                <Card sx={{ mb: 3 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6" gutterBottom>
                                                CV Information
                                            </Typography>
                                            {cvId && (
                                                <Button
                                                    component={Link}
                                                    to={`/cv/${cvId}`}
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                >
                                                    View Full CV
                                                </Button>
                                            )}
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="h6">
                                                    {cv.name || ''} {cv.last_name || ''}
                                                </Typography>
                                                {cv.title && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {cv.title}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            {cv.email && (
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <EmailIcon fontSize="small" color="action" />
                                                        <Typography variant="body2">
                                                            {cv.email}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {cv.phone && (
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PhoneIcon fontSize="small" color="action" />
                                                        <Typography variant="body2">
                                                            {cv.phone}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {(cv.address || cv.city || cv.country) && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LocationOnIcon fontSize="small" color="action" />
                                                        <Typography variant="body2">
                                                            {[cv.address, cv.city, cv.country?.name || cv.country].filter(Boolean).join(', ')}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {cv.website && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LanguageIcon fontSize="small" color="action" />
                                                        <Typography variant="body2" component="a" href={cv.website} target="_blank" sx={{ textDecoration: 'none', color: 'primary.main' }}>
                                                            {cv.website}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                            {cv.description && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Description
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {cv.description}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {cv.interests && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Interests
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {cv.interests}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )}

                            {/* CV File */}
                            {cvFile && (
                                <Card sx={{ mb: 3 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            CV File
                                        </Typography>
                                        <Divider sx={{ my: 2 }} />
                                        {cvFile.title && (
                                            <Typography variant="body1" gutterBottom>
                                                {cvFile.title}
                                            </Typography>
                                        )}
                                        {cvFile.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {cvFile.description}
                                            </Typography>
                                        )}
                                        {getCVFileUrl() && (
                                            <Button
                                                variant="contained"
                                                startIcon={<PictureAsPdfIcon />}
                                                href={getCVFileUrl()}
                                                target="_blank"
                                                download
                                            >
                                                Download CV File
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* CV Sections */}
                            {cv && cvSections && (
                                <>
                                    {/* Job Experience */}
                                    {cvSections.experiences && cvSections.experiences.length > 0 && (
                                        <Card sx={{ mb: 3 }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <WorkIcon color="primary" />
                                                    <Typography variant="h6">
                                                        Work Experience
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <List>
                                                    {cvSections.experiences.map((exp) => (
                                                        <ListItem key={exp.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {exp.title}
                                                            </Typography>
                                                            {exp.company_title && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {exp.company_title}
                                                                </Typography>
                                                            )}
                                                            <Typography variant="body2" color="text.secondary">
                                                                {exp.start_date && new Date(exp.start_date).toLocaleDateString()} - {exp.end_date ? new Date(exp.end_date).toLocaleDateString() : 'Present'}
                                                            </Typography>
                                                            {exp.description && (
                                                                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                                                                    {exp.description}
                                                                </Typography>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Education */}
                                    {cvSections.education && cvSections.education.length > 0 && (
                                        <Card sx={{ mb: 3 }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <SchoolIcon color="primary" />
                                                    <Typography variant="h6">
                                                        Education
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <List>
                                                    {cvSections.education.map((edu) => (
                                                        <ListItem key={edu.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {edu.level} {edu.field && `- ${edu.field}`}
                                                            </Typography>
                                                            {edu.location && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {edu.location}
                                                                </Typography>
                                                            )}
                                                            <Typography variant="body2" color="text.secondary">
                                                                {edu.start_date && new Date(edu.start_date).toLocaleDateString()} - {edu.end_date ? new Date(edu.end_date).toLocaleDateString() : 'Present'}
                                                            </Typography>
                                                            {edu.description && (
                                                                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                                                                    {edu.description}
                                                                </Typography>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Skills */}
                                    {cvSections.skills && cvSections.skills.length > 0 && (
                                        <Card sx={{ mb: 3 }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <StarIcon color="primary" />
                                                    <Typography variant="h6">
                                                        Skills
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {cvSections.skills.map((skill) => (
                                                        <Chip
                                                            key={skill.id}
                                                            label={skill.title}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Languages */}
                                    {cvSections.languages && cvSections.languages.length > 0 && (
                                        <Card sx={{ mb: 3 }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <LanguageIcon color="primary" />
                                                    <Typography variant="h6">
                                                        Languages
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <List>
                                                    {cvSections.languages.map((lang) => (
                                                        <ListItem key={lang.id}>
                                                            <ListItemText
                                                                primary={lang.title}
                                                                secondary={lang.proficiency_level || 'Not specified'}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Certificates */}
                                    {cvSections.certificates && cvSections.certificates.length > 0 && (
                                        <Card sx={{ mb: 3 }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <CardMembershipIcon color="primary" />
                                                    <Typography variant="h6">
                                                        Certifications
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <List>
                                                    {cvSections.certificates.map((cert) => (
                                                        <ListItem key={cert.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {cert.title}
                                                            </Typography>
                                                            {cert.issuing_organization && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {cert.issuing_organization}
                                                                </Typography>
                                                            )}
                                                            {cert.issue_date && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Issued: {new Date(cert.issue_date).toLocaleDateString()}
                                                                </Typography>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* References */}
                                    {cvSections.references && cvSections.references.length > 0 && (
                                        <Card sx={{ mb: 3 }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Typography variant="h6" gutterBottom>
                                                    References
                                                </Typography>
                                                <Divider sx={{ my: 2 }} />
                                                <List>
                                                    {cvSections.references.map((ref) => (
                                                        <ListItem key={ref.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {ref.name}
                                                            </Typography>
                                                            {ref.position && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {ref.position}
                                                                </Typography>
                                                            )}
                                                            {ref.company && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {ref.company}
                                                                </Typography>
                                                            )}
                                                            {ref.email && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {ref.email}
                                                                </Typography>
                                                            )}
                                                            {ref.phone && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {ref.phone}
                                                                </Typography>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    )}
                                </>
                            )}

                            {cv && cvSectionsLoading && (
                                <Card sx={{ mb: 3 }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                        <CircularProgress />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                            Loading CV details...
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}

                            {!cv && !cvFile && (
                                <Card sx={{ mb: 3 }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No CV information available for this application.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>

                        {/* Right Column - Decision Panel */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ position: 'sticky', top: 20 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        Application Decision
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />
                                    
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Current Status
                                        </Typography>
                                        <Chip
                                            label={application.final_decision || 'Pending'}
                                            color={
                                                application.final_decision === 'hired' ? 'success' :
                                                application.final_decision === 'shortlisted' ? 'primary' :
                                                application.final_decision === 'rejected' ? 'error' :
                                                'default'
                                            }
                                            sx={{ mb: 2 }}
                                        />
                                    </Box>

                                    <TextField
                                        fullWidth
                                        select
                                        label="Decision"
                                        value={decision}
                                        onChange={(e) => setDecision(e.target.value)}
                                        sx={{ mb: 2 }}
                                    >
                                        <MenuItem value="">Pending</MenuItem>
                                        <MenuItem value="shortlisted">Shortlisted</MenuItem>
                                        <MenuItem value="rejected">Rejected</MenuItem>
                                        <MenuItem value="hired">Hired</MenuItem>
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Decision Notes"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        sx={{ mb: 2 }}
                                        placeholder="Add any notes about this application..."
                                    />

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Decision'}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </section>
    );
}

export default EmployerApplicationDetailsPage;

